<script lang="ts">
  // Player card, the same VN stage as the NPC dialog, but the figure is the clicked
  // player's OWN customised avatar (full bust), and the band shows their GX standing
  // plus Duel / Trade actions instead of dialogue.
  import type { PlayerProfile } from "@ygoevo/world-protocol";
  import PortraitPreview from "./PortraitPreview.svelte";
  import RankBadge from "./RankBadge.svelte";
  import { chibiFrom } from "../../render/ChibiAvatar.ts";
  import { onEscape } from "../hotkeys.ts";

  let {
    profile,
    onClose,
    onDuel,
    onTrade,
    onGoTo,
  }: {
    profile: PlayerProfile;
    onClose: () => void;
    onDuel: () => void;
    onTrade: () => void;
    onGoTo: () => void;
  } = $props();

  let innerH = $state(800);
  const bustH = $derived(Math.round(Math.min(innerH * 0.82, 760)));
  const look = $derived(chibiFrom(profile.avatar));
  const r = $derived(profile.ranking);
  const total = $derived(r.wins + r.losses);
  const winRate = $derived(total > 0 ? Math.round((r.wins / total) * 100) : 0);

  const reduce =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  onEscape(() => onClose());
  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };
</script>

<svelte:window bind:innerHeight={innerH} />

<div class="vn fixed inset-0 z-50 overflow-hidden" role="presentation">
  <button class="modal-scrim absolute inset-0 bg-void/50" onclick={onClose} aria-label="Close"></button>
  <div class="vn-vignette pointer-events-none absolute inset-0"></div>
  <div class="grid-tex pointer-events-none absolute inset-0 opacity-40"></div>

  <!-- the player's full avatar, rising from behind the band -->
  <div class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
    <div class="vn-spot absolute bottom-0 left-1/2 -translate-x-1/2"></div>
    <div class="relative" class:vn-bust-anim={!reduce}>
      <PortraitPreview size={bustH} {look} />
      <div class="vn-ground absolute inset-x-0 bottom-0"></div>
    </div>
  </div>

  <button
    class="vn-x chamfer-btn absolute right-4 top-4 z-10 grid size-9 place-items-center bg-surface-2/80 text-ink-dim transition-colors hover:text-magenta hover:glow-magenta"
    style="--c:7px"
    onclick={stop(onClose)}
    aria-label="Close"
  >
    <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
  </button>

  <!-- info band -->
  <div class="absolute inset-x-0 bottom-0 flex justify-center px-3 pb-3 sm:px-6 sm:pb-6">
    <div class="world-vn-panel relative w-full max-w-[1080px]">
      <div class="vn-name chamfer absolute -top-4 left-5 z-10 flex items-baseline bg-cyan px-4 py-1 glow-cyan" style="--c:8px">
        <span class="font-display text-base font-bold uppercase leading-none tracking-[0.08em] text-void">{profile.name}</span>
        {#if !profile.online}
          <span class="ml-2 font-display text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-void/60">Offline</span>
        {/if}
      </div>

      <div class="chamfer bg-cyan/70 glow-cyan p-px" style="--c:16px">
        <div class="chamfer grid-tex relative bg-surface/95 px-6 pb-5 pt-7 sm:px-8" style="--c:15px">
          <div
            class="pointer-events-none absolute inset-x-8 top-0 h-px"
            style="background:linear-gradient(90deg,transparent,color-mix(in oklab,var(--color-cyan) 80%,transparent),transparent)"
          ></div>

          <div class="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
            <!-- standing + stats -->
            <div class="flex flex-col gap-2.5">
              <RankBadge rank={r} size="md" />
              <div class="flex flex-wrap items-center gap-x-6 gap-y-1.5">
                {#snippet stat(label: string, value: string | number)}
                  <span class="leading-tight">
                    <span class="block font-display text-base font-bold tabular-nums text-ink">{value}</span>
                    <span class="block font-display text-[0.58rem] uppercase tracking-[0.16em] text-ink-dim">{label}</span>
                  </span>
                {/snippet}
                {@render stat("Record", `${r.wins}–${r.losses}`)}
                {@render stat("Win rate", `${winRate}%`)}
                {@render stat("Games", r.games)}
                {@render stat("Peak", r.peak)}
              </div>
            </div>

            <!-- actions -->
            <div class="flex items-center gap-2.5">
              <button
                onclick={stop(onGoTo)}
                disabled={!profile.online}
                class="chamfer-btn flex items-center gap-2 bg-lime/15 px-6 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-lime transition-colors hover:bg-lime/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-lime/15"
                style="--c:9px"
              >
                <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                Go to
              </button>
              <button
                onclick={stop(onTrade)}
                disabled={!profile.online}
                class="chamfer-btn flex items-center gap-2 bg-cyan/15 px-6 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-cyan transition-colors hover:bg-cyan/25 hover:glow-cyan disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-cyan/15"
                style="--c:9px"
              >
                <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7 3 7" /><path d="m7 3-4 4 4 4" /><path d="M3 17h18" /><path d="m17 13 4 4-4 4" /></svg>
                Trade
              </button>
              <button
                onclick={stop(onDuel)}
                disabled={!profile.online}
                class="chamfer-btn flex items-center gap-2 bg-magenta/15 px-6 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-magenta transition-colors hover:bg-magenta/25 hover:glow-magenta disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-magenta/15"
                style="--c:9px"
              >
                <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" /><line x1="13" x2="19" y1="19" y2="13" /><line x1="16" x2="20" y1="16" y2="20" /><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" /><line x1="5" x2="9" y1="14" y2="18" /></svg>
                Duel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .vn {
    animation: vn-in 200ms ease-out;
  }
  @keyframes vn-in {
    from {
      opacity: 0;
    }
  }
  .vn-vignette {
    background: radial-gradient(120% 90% at 50% 38%, transparent 40%, color-mix(in oklab, var(--color-void) 92%, transparent) 100%);
  }
  .vn-spot {
    width: min(70vw, 720px);
    height: min(70vh, 720px);
    background: radial-gradient(closest-side, color-mix(in oklab, var(--color-cyan) 22%, transparent), transparent 72%);
    filter: blur(8px);
  }
  .vn-ground {
    height: 38px;
    background: radial-gradient(closest-side, color-mix(in oklab, var(--color-cyan) 38%, transparent), transparent);
    filter: blur(3px);
  }
  .vn-bust-anim {
    animation: vn-rise 420ms cubic-bezier(0.16, 1, 0.3, 1), vn-float 6s ease-in-out 420ms infinite;
  }
  @keyframes vn-rise {
    from {
      opacity: 0;
      transform: translateY(28px);
    }
  }
  @keyframes vn-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .vn-bust-anim {
      animation: none;
    }
  }
</style>
